import { type ElementRef, useEffect, useRef, useState } from 'react'

import { useTimer } from 'react-use-precision-timer'
import styled from 'styled-components'

import { useAudioStore } from 'context/AudioContext/AudioContext'
import { Icons } from 'helpers/icons'
import { formatVideoTime } from 'lib/dayjs'
import colors from 'styles/colors'

type WaveColors = 'white' | 'blue' | 'gray'
type WaveBackgroundColor = 'white' | 'blue' | 'transparent'

interface IAudioWaveProps {
    waveColor?: WaveColors
    waveOverlayColor?: WaveColors
    waveOverlayOpacity?: number
    waveHeight?: number
    waveBackgroundColor?: WaveBackgroundColor
    timerTextColor?: string
    timerTextSize?: number
    timerTextWidth?: number
    hasTimer?: boolean
    audioRef: ElementRef<'audio'> | null
}

export const AudioWave = ({
    waveColor = 'white',
    waveOverlayColor = 'blue',
    waveBackgroundColor = 'blue',
    waveOverlayOpacity = 1,
    timerTextSize = 10,
    timerTextColor = 'gray',
    timerTextWidth = 40,
    waveHeight = 20,
    hasTimer = false,
    audioRef
}: IAudioWaveProps) => {
    const state = useAudioStore()
    const [dragging, setDragging] = useState(false)
    const [durationPreview, setDurationPreview] = useState<string>('')

    const {
        setPlaying,
        setFullyListened,
        listened,
        setListened,
        playing,
        fullyListened,
        duration
    } = state

    const waveRef = useRef<ElementRef<'div'>>(null)

    const listenedTimer = useTimer(
        {
            delay: 100
        },
        () => {
            setListened(listened + 0.1)
        }
    )

    useEffect(() => {
        if (playing) {
            listenedTimer.start()
        } else {
            listenedTimer.pause()
        }
        if (fullyListened) {
            setListened(duration)
            listenedTimer.stop()
        }
    }, [playing, fullyListened, listenedTimer])

    useEffect(() => {
        if (duration) {
            const formattedDurationPosition = formatVideoTime(
                duration - listened
            )
            setDurationPreview(formattedDurationPosition)
        }
    }, [duration, listened])

    const calcAudioRewindProgress = (e: any) => {
        let durationTime = 0
        if (waveRef?.current) {
            const targetRect = waveRef?.current.getBoundingClientRect()
            const clientX = e.touches ? e.touches[0].clientX : e.clientX
            let x = clientX - targetRect.left

            if (x < 0) {
                x = 0
            }
            if (x >= targetRect.width) {
                x = targetRect.width
            }
            const widthPercentage = x / targetRect.width

            durationTime = duration * widthPercentage
        }
        return durationTime
    }

    const handleMouseDown = (e: any) => {
        setDragging(true)
        setPlaying(false)
        setFullyListened(false)
        if (audioRef) {
            audioRef.pause()
            const durationTime = calcAudioRewindProgress(e)
            setListened(durationTime)
            audioRef.currentTime = durationTime
        }
    }

    const handleMouseMove = (e: any) => {
        if (dragging) {
            setPlaying(false)
            setFullyListened(false)
            if (audioRef) {
                audioRef.pause()
                const durationTime = calcAudioRewindProgress(e)
                setListened(durationTime)
                audioRef.currentTime = durationTime
            }
        }
    }

    const handleMouseUp = () => {
        setDragging(false)
        if (audioRef) {
            audioRef.play()
            setPlaying(true)
        }
    }

    return (
        <AudioWaveContainer
            $waveBackgroundColor={waveBackgroundColor}
            $timerTextColor={timerTextColor}
            $timerTextSize={timerTextSize}
            $timerTextWidth={timerTextWidth}
        >
            <Waveform
                ref={waveRef}
                $backgroundImage={Icons[`audio_waveform_${waveColor}`]}
                $waveHeight={waveHeight}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onTouchMove={handleMouseMove}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
            >
                <WaveformOverlay
                    $width={Math.round((listened / duration) * 100)}
                    $opacity={waveOverlayOpacity}
                >
                    <Waveform
                        $backgroundImage={
                            Icons[`audio_waveform_${waveOverlayColor}`]
                        }
                    ></Waveform>
                </WaveformOverlay>
            </Waveform>

            {hasTimer && (
                <PreviewBarsLength
                    $timerTextColor={timerTextColor}
                    $timerTextSize={timerTextSize}
                    $timerTextWidth={timerTextWidth}
                >
                    {durationPreview}
                </PreviewBarsLength>
            )}
        </AudioWaveContainer>
    )
}

interface IWaveformProps {
    $waveHeight?: number
    $backgroundImage: string
}

const Waveform = styled.div<IWaveformProps>`
    background-position: left center;
    background-size: contain;
    background-repeat: repeat-x;
    width: 100vw;
    min-height: ${props => `${props.$waveHeight}px`};
    height: 100%;
    position: relative;
    overflow: hidden;
    user-select: none;
    outline: none;
    background-image: ${props => `url(${props.$backgroundImage})`};
`

interface IWaveformOverlayProps {
    $width: number
    $opacity: number
}

const WaveformOverlay = styled.div<IWaveformOverlayProps>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 3;
    overflow: hidden;
    width: 0;
    height: 100%;
    width: ${props => `${props.$width}%`};
    opacity: ${props => props.$opacity};
    transition: width 0.1s ease-in-out;
`

interface IAudioWaveContainerProps {
    $timerTextColor?: string
    $timerTextSize?: number
    $timerTextWidth?: number
    $waveBackgroundColor: WaveBackgroundColor
}

const AudioWaveContainer = styled.div<IAudioWaveContainerProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
    flex: 1;
    padding: 0.5rem;
    height: 100%;
    border-radius: 1rem;

    ${props => {
        if (props.$waveBackgroundColor === 'blue') {
            return `background: ${colors.blue92deg};`
        } else {
            return `background: ${props.$waveBackgroundColor}`
        }
    }}
`

interface IPreviewBarsLength {
    $timerTextColor: string
    $timerTextSize: number
    $timerTextWidth: number
}

const PreviewBarsLength = styled.div<IPreviewBarsLength>`
    color: ${props => props.$timerTextColor};
    font-size: ${props => `${props.$timerTextSize}px`};
    width: ${props => `${props.$timerTextWidth}px`};
`
